/**
 * The `location.state` shape carried from the household sheet
 * (`/sessions/:id/door`) to the registration door
 * (`/sessions/:id/door/registration`): `GET /doors/{id}/` does not exist
 * (`docs/API.md` §4), so the door's identity travels through navigation
 * state instead of a second fetch.
 */

/** `Address.housing_type`, inlined here to avoid a dependency on the full `Address` type. */
export type HousingType = 'house' | 'building' | 'unknown';

export interface RegistrationDoorNavigationState {
  doorId: number;
  doorLabel: string;
  addressLabel: string;
  /** The visit just recorded at this door, linked on the opened case. */
  visitId?: number;
  housingType?: HousingType;
  /** The phantom door's H1/H2 inference score in [0, 1], when known (`docs/PHANTOM.md` §5). */
  confidence?: number | null;
  /** The phantom door's inference hypothesis sentence, when known. */
  hypothesis?: string | null;
}

/**
 * Narrow an unknown `location.state` value into a
 * {@link RegistrationDoorNavigationState}, or `undefined` when it does not
 * match — e.g. the registration door screen was opened directly, without
 * going through the household sheet first.
 *
 * @param state - `location.state`, as handed over by `useLocation()`.
 * @returns The navigation state, or `undefined`.
 */
export function asRegistrationDoorState(
  state: unknown,
): RegistrationDoorNavigationState | undefined {
  if (!state || typeof state !== 'object') return undefined;
  const candidate = state as Partial<RegistrationDoorNavigationState>;
  return typeof candidate.doorId === 'number' &&
    typeof candidate.doorLabel === 'string' &&
    typeof candidate.addressLabel === 'string'
    ? (candidate as RegistrationDoorNavigationState)
    : undefined;
}
